import { HttpClient } from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { isAunakProAppRole } from 'src/app/shared/constants/aunak-pro-app-roles';
import { AuthSessionService } from 'src/app/shared/services/auth-session.service';

const BACKEND_BASE = 'https://squid-app-4ndr5.ondigitalocean.app';

/**
 * Mobile OTP auth lives in Capacitor Browser, which persists Firebase cookies.
 * The native app is the source of truth for login state — never auto-handoff a
 * leftover web session back into the app when opening Login/Register.
 */
export function isMobileOtpAuthPath(url: string): boolean {
  const path = (url || '').split('?')[0];
  return (
    path === '/signin' ||
    path === '/signup' ||
    path.startsWith('/signin/') ||
    path.startsWith('/signup/')
  );
}

export function isMobileAppWebview(): boolean {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get('mobile') === 'true' ||
    /CapacitorHttp/i.test(navigator.userAgent)
  );
}

export function redirectToMobileApp(customToken: string): void {
  window.location.href = `aunak://auth-callback?token=${encodeURIComponent(
    customToken,
  )}`;
}

export function redirectMobileSessionCleared(): void {
  window.location.href = 'aunak://auth-session-cleared';
}

function wantsLogoutClear(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get('logout') === '1';
}

/**
 * Always clear any Capacitor Browser Firebase/web session when opening mobile
 * OTP login/register (or explicit logout clear from the app).
 *
 * Returns:
 * - `cleared_and_closed` — app asked to wipe session then close Browser
 * - `signed_out` — wiped an existing session; show OTP form
 * - `guest` — already signed out
 */
export async function resolveMobileWebviewSession(opts: {
  afAuth: AngularFireAuth;
  afs: AngularFirestore;
  http: HttpClient;
  session?: AuthSessionService;
}): Promise<'cleared_and_closed' | 'signed_out' | 'guest'> {
  if (!isMobileAppWebview()) {
    return 'guest';
  }

  const user = await opts.afAuth.currentUser;
  let hadSession = !!user;

  try {
    if (user) {
      await opts.afAuth.signOut();
    }
  } catch (err) {
    console.warn('[MobileWebview] signOut failed', err);
  }

  try {
    opts.session?.clear();
  } catch {
    // ignore
  }

  // Drop common auth leftovers so the next OTP is a clean start.
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (
        key.startsWith('firebase:') ||
        key.includes('firebaseui') ||
        key === 'loggedUser'
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }

  if (wantsLogoutClear()) {
    redirectMobileSessionCleared();
    return 'cleared_and_closed';
  }

  return hadSession ? 'signed_out' : 'guest';
}

/**
 * Explicit "return to app" handoff (e.g. Aunak Pro landing button).
 * Not used on Login/Register open — those always force a fresh OTP.
 */
export async function handoffMobileWebviewSessionIfLoggedIn(opts: {
  afAuth: AngularFireAuth;
  afs: AngularFirestore;
  http: HttpClient;
  session?: AuthSessionService;
}): Promise<'handed_off' | 'signed_out' | 'guest'> {
  const user = await opts.afAuth.currentUser;
  if (!user) {
    return 'guest';
  }

  let role: string | undefined;
  try {
    const snap = await opts.afs.collection('users').doc(user.uid).get().toPromise();
    role = (snap?.data() as { role?: string } | undefined)?.role;
  } catch {
    role = undefined;
  }

  if (isAunakProAppRole(role)) {
    try {
      const idToken = await user.getIdToken();
      const response: any = await opts.http
        .post(`${BACKEND_BASE}/firebase/custom-token`, { idToken })
        .toPromise();
      if (response?.success && response?.token) {
        redirectToMobileApp(response.token);
        return 'handed_off';
      }
    } catch (err) {
      console.warn('[MobileWebview] Failed to hand off existing session', err);
    }
  }

  await opts.afAuth.signOut();
  opts.session?.clear();
  return 'signed_out';
}

